import React from 'react';
import { Alert, Button, Col, FloatingLabel, Form, Row, Spinner } from 'react-bootstrap';
import { allDepartments } from '@src/Api';
import type { DynamicsBcSettingsModuleProps } from '@src/pages/settings/types';

interface Department {
  id: string;
  code: string;
  displayName: string;
}
interface Choices {
  scope: string;
  dimension: { id: string; code: string } | null;
  values: Department[];
}

export const DefaultDepartment: React.FC<DynamicsBcSettingsModuleProps> = ({ dynamicsBcUserDefaultData, setDynamicsBcUserDefaultData }) => {
  const [choices, setChoices] = React.useState<Choices>();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [reload, setReload] = React.useState(0);
  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const load = async () => {
      try {
        const response = await allDepartments();
        if (!active) return;
        const data = response?.data;
        if (
          response?.status !== 200 ||
          typeof data?.scope !== 'string' ||
          !(data.dimension === null || (typeof data.dimension?.id === 'string' && data.dimension.code === 'AFDELING')) ||
          !Array.isArray(data.values) ||
          !data.values.every((value: Department) => typeof value.id === 'string' && typeof value.code === 'string' && typeof value.displayName === 'string')
        )
          throw new Error('Unable to load Business Central departments. Check the selected company and application permissions.');
        setChoices(data);
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : 'Unable to load departments.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [reload]);
  const selected = dynamicsBcUserDefaultData.invoice_department;
  const otherConnection = !!selected && !!choices && selected.scope !== choices.scope;
  const unavailable =
    !!selected &&
    !!choices &&
    (selected.dimensionId !== choices.dimension?.id || !choices.values.some((value) => value.id === selected.valueId && value.code === selected.valueCode));
  return (
    <Form.Group as={Row} className="mb-3" controlId="default-invoice-department">
      <Form.Label column sm={6}>
        Department
      </Form.Label>
      <Col sm={6}>
        <FloatingLabel controlId="default-invoice-department" label="Select default department">
          <Form.Select
            aria-describedby="default-invoice-department-description"
            value={selected && (otherConnection || unavailable) ? `saved:${selected.valueId}` : selected?.valueId || ''}
            disabled={loading || !!error || !choices || !setDynamicsBcUserDefaultData}
            isInvalid={!loading && !error && (otherConnection || unavailable)}
            onChange={(event) => {
              const value = choices?.values.find((entry) => entry.id === event.target.value);
              const dimension = choices?.dimension;
              if (event.target.value && (!value || !dimension)) return;
              setDynamicsBcUserDefaultData?.((previous) => {
                const next = { ...previous };
                if (value && dimension && choices)
                  next.invoice_department = {
                    scope: choices.scope,
                    dimensionId: dimension.id,
                    dimensionCode: dimension.code,
                    valueId: value.id,
                    valueCode: value.code,
                  };
                else delete next.invoice_department;
                return next;
              });
            }}
          >
            <option value="">No default department</option>
            {selected && (otherConnection || unavailable) && (
              <option value={`saved:${selected.valueId}`} disabled>
                {selected.valueCode} (saved for another connection or unavailable)
              </option>
            )}
            {choices?.values.map((value) => (
              <option key={value.id} value={value.id}>
                {value.code} — {value.displayName}
              </option>
            ))}
          </Form.Select>
        </FloatingLabel>
        <Form.Text id="default-invoice-department-description" className="d-block mt-2" muted>
          Optional. Apply this department to new invoices and credit notes, including their lines. Existing documents stay unchanged.
        </Form.Text>
        {loading && (
          <Form.Text className="d-block mt-2" role="status" muted>
            <Spinner as="span" animation="border" size="sm" /> Loading departments…
          </Form.Text>
        )}
        {error && (
          <Alert variant="danger" className="mt-2 mb-0">
            {error}{' '}
            <Button size="sm" variant="outline-danger" onClick={() => setReload((value) => value + 1)}>
              Retry
            </Button>
          </Alert>
        )}
        {otherConnection && (
          <Alert variant="warning" className="mt-2 mb-0">
            The saved department belongs to a different company or environment. Select a department for this connection.
          </Alert>
        )}
        {!loading && !error && choices && !choices.dimension && (
          <Alert variant="info" className="mt-2 mb-0">
            The selected company has no AFDELING dimension.
          </Alert>
        )}
      </Col>
    </Form.Group>
  );
};
